/**
 * The navigation icons: one small stroke glyph per screen, drawn inline so
 * there is no icon font or library to load and they take the current text
 * colour. Keep them simple — at 18 px a glyph is a hint beside the label.
 */

export type IconName =
  | "platform"
  | "dashboard"
  | "recipes"
  | "wheel"
  | "chat"
  | "books"
  | "stock"
  | "prep"
  | "menu"
  | "wastage"
  | "suppliers"
  | "orders"
  | "expenses"
  | "cashup"
  | "sales"
  | "org"
  | "queue"
  | "whattail"
  | "guests"
  | "training"
  | "service"
  | "develop"
  | "stockGroup"
  | "reports"
  | "admin"
  | "account"
  | "close";

/** Paths in a 24 × 24 box, stroked, no fill. */
const PATHS: Readonly<Record<IconName, string>> = {
  // a globe: the platform above every organisation
  platform: "M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18Z M3 12h18 M12 3c-3 3-3 15 0 18 M12 3c3 3 3 15 0 18",
  // four tiles
  dashboard: "M4 4h6v6H4Z M14 4h6v6h-6Z M4 14h6v6H4Z M14 14h6v6h-6Z",
  // an open book
  recipes: "M12 6c-2-1.5-5-1.5-8 0v13c3-1.5 6-1.5 8 0 M12 6c2-1.5 5-1.5 8 0v13c-3-1.5-6-1.5-8 0 M12 6v13",
  // a wheel: a ring with spokes
  wheel: "M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18Z M12 8a4 4 0 1 0 0 8a4 4 0 0 0 0-8Z M12 3v5 M12 16v5 M3 12h5 M16 12h5",
  // a speech bubble
  chat: "M4 5h16v11H9l-5 4Z",
  // three spines on a shelf, the last one leaning
  books: "M4 20h16 M5 20V6h3v14 M9 20V4h3v16 M14 20l2-14 3 .5-2 13.5",
  // a bottle
  stock: "M10 3h4v3l2 3v11a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V9l2-3Z M8 14h8",
  // a mortarboard: what the bar teaches
  training: "M12 5 3 9l9 4 9-4-9-4Z M7 11v5c0 1.5 2.2 3 5 3s5-1.5 5-3v-5 M20 9.5V15",
  // a ticked list
  prep: "M4 6l2 2 3-3 M4 12l2 2 3-3 M4 18l2 2 3-3 M12 7h8 M12 13h8 M12 19h8",
  // a scatter plot
  menu: "M4 20V4 M4 20h16 M9 13a1 1 0 1 0 0-2a1 1 0 0 0 0 2Z M14 9a1 1 0 1 0 0-2a1 1 0 0 0 0 2Z M16 15a1 1 0 1 0 0-2a1 1 0 0 0 0 2Z M10 17a1 1 0 1 0 0-2a1 1 0 0 0 0 2Z",
  // a drop
  wastage: "M12 3s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11Z",
  // a list beside a clock face: people waiting their turn
  whattail: "M3.5 5h11l-5.5 6.5z M9 11.5V19 M6 19.5h6 M17 8.6a2 2 0 1 1 2.7 1.9c-.5.2-.7.6-.7 1.2 M19 14.6v.2",
  queue: "M4 6h9 M4 11h9 M4 16h6 M17.5 13a3.5 3.5 0 1 0 0 7a3.5 3.5 0 0 0 0-7Z M17.5 15v1.7l1.2 1",
  // two heads and shoulders
  guests: "M9 11a3 3 0 1 0 0-6a3 3 0 0 0 0 6Z M3.5 20c0-3 2.5-5.5 5.5-5.5s5.5 2.5 5.5 5.5 M15.5 5.3a3 3 0 0 1 0 5.4 M17 14.8c2 .7 3.5 2.6 3.5 5.2",
  // a delivery truck
  suppliers: "M3 7h11v9H3Z M14 10h4l3 3v3h-7Z M7 19a2 2 0 1 0 0-4a2 2 0 0 0 0 4Z M17 19a2 2 0 1 0 0-4a2 2 0 0 0 0 4Z",
  // a basket
  orders: "M4 9h16l-2 10H6Z M8 9l4-6 4 6 M9 13v3 M15 13v3",
  // a receipt
  expenses: "M6 3h12v18l-2-2-2 2-2-2-2 2-2-2-2 2Z M9 8h6 M9 12h6 M9 16h4",
  // a till: the drawer under the register, a coin above it
  cashup: "M4 12h16v8H4Z M7 16h4 M15 16h2 M6 12V8h12v4 M12 8a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5Z",
  // three bars rising from a baseline: the nights' takings
  sales: "M4 20h16 M7 20v-5 M12 20v-9 M17 20V6",
  // people
  org: "M9 11a3 3 0 1 0 0-6a3 3 0 0 0 0 6Z M3 20c0-3 3-5 6-5s6 2 6 5 M17 10a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5Z M21 19c0-2.5-2-4-4-4",
  // ---- the navigation groups
  // a coupe: what happens during service
  service: "M4 5h16l-8 8Z M12 13v7 M8 20h8",
  // a flask: working a drink out
  develop: "M9 3h6 M10 3v6l-5 9a2 2 0 0 0 1.7 3h10.6a2 2 0 0 0 1.7-3l-5-9V3 M7.5 15h9",
  // a box: what is on the shelf and coming in
  stockGroup: "M3 7.5l9-4.5 9 4.5v9l-9 4.5-9-4.5Z M3 7.5l9 4.5 9-4.5 M12 12v9",
  // a rising line: the figures
  reports: "M4 20h16 M4 16l5-5 4 3 7-7 M15 7h5v5",
  // sliders: settings and the people in them
  admin: "M4 6h9 M17 6h3 M4 12h3 M11 12h9 M4 18h11 M19 18h1 M15 4v4 M9 10v4 M17 16v4",
  // a person: who is signed in, and where
  account: "M12 12a4 4 0 1 0 0-8a4 4 0 0 0 0 8Z M4 21c0-4 4-6 8-6s8 2 8 6",
  close: "M6 6l12 12 M18 6L6 18",
};

export function Icon({ name, size = 18 }: { readonly name: IconName; readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
